import React, {useEffect, useMemo, useState} from 'react';

import {
  DNA_PAIR_TAG_SCHEMA_NAME,
  MAX_ANTIBODIES_TO_CREATE,
} from 'antibodies/nested-data-creation/constants/AntibodyNestedDataCreationConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section} from 'constants/NavConstants';
import {TagSchemaSupportedFolderItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import BioentitySearchView, {clearBioentitySearchViewSearchParams} from 'molbio/BioentitySearchView';
import {ItemListingSchemasQuery} from 'nav/sidepanel/listing/__generated/ItemListingSchemasQuery';
import {SchemasFilterFunction, ItemListingSchemasByType} from 'nav/sidepanel/listing/ItemListingUtil';
import notifications from 'notifications';
import {TagSchemasWithRegistryQuery} from 'schemas/util/__generated/TagSchemasWithRegistryQuery';
import {useTagSchemasWithRegistryQuery} from 'schemas/util/useTagSchemasQuery';
import {TypesFilterSchemasQuery} from 'search/filters/__generated/TypesFilterSchemasQuery';
import {FilterKey, keyToEmptyFilter, Operator} from 'search/SearchConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';

// Hook to fetch the DNA pair tag schema. Returns null if schema is inaccessible or nonexistent.
function usePairTagSchema(): {
  pairTagSchema: TagSchemasWithRegistryQuery['tagSchemas'][number] | null;
  isLoading: boolean;
} {
  const result = useTagSchemasWithRegistryQuery();

  const pairTagSchema = useMemo(() => {
    if (result.type === QueryResultType.SUCCESS) {
      return result.data.tagSchemas.find((schema) => schema.name === DNA_PAIR_TAG_SCHEMA_NAME) || null;
    }
    return null;
  }, [result]);

  return {
    pairTagSchema,
    isLoading: result.type === QueryResultType.PENDING,
  };
}

function getFilterToPairSchemaFunction(pairSchemaId: string): SchemasFilterFunction {
  return (schemasByAvailableType: ItemListingSchemasByType): ItemListingSchemasByType => {
    const filteredSchemas: ItemListingSchemasByType = {};
    for (const type of Object.keys(schemasByAvailableType)) {
      const tagSchemas = schemasByAvailableType[type] as Array<
        TypesFilterSchemasQuery['tagSchemas'][number] | ItemListingSchemasQuery['tagSchemas'][number]
      >;
      const componentSchemas = tagSchemas.filter((schema) => schema.id === pairSchemaId);
      filteredSchemas[type] = componentSchemas;
    }

    return filteredSchemas;
  };
}

interface SearchForDnaPairsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (selectedIds: Array<string>) => void;
}

export default function SearchForDnaPairsModal({
  onRequestClose,
  onRequestSubmit,
}: SearchForDnaPairsModalProps): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);
  const {pairTagSchema, isLoading} = usePairTagSchema();

  useEffect(() => {
    if (pairTagSchema != null) {
      if (pairTagSchema.folderItemType !== TagSchemaSupportedFolderItemType.basic_folder_item) {
        notifications.error(`Schema ${DNA_PAIR_TAG_SCHEMA_NAME} must be a custom entity schema`);
        onRequestClose();
      }
      clearBioentitySearchViewSearchParams(
        [ModelTypeConstants.BASIC_FOLDER_ITEM],
        Section.MOLBIO_BIOINTELLIGENT_LINK_COMPONENT_REPLACE,
        [
          {
            ...keyToEmptyFilter(FilterKey.SCHEMA),
            operator: Operator.IS_ONE_OF,
            value: [pairTagSchema.id],
            locked: true,
          },
        ]
      );
    } else if (!isLoading) {
      notifications.error(`A schema ${DNA_PAIR_TAG_SCHEMA_NAME} is required`);
      onRequestClose();
    }
  }, [pairTagSchema, isLoading, onRequestClose]);

  if (!isLoading && pairTagSchema == null) {
    return <></>;
  }
  return (
    <Modal
      fullWidth={true}
      fullHeight={true}
      scrollable={false}
      title="Search for pairs"
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: 'Select',
          style: ButtonStyle.SECONDARY,
          submit: true,
          disabled: selectedIds.length === 0,
        },
      ]}
      onRequestClose={() => {
        setSelectedIds([]);
        onRequestClose();
      }}
      onRequestSubmit={() => {
        onRequestSubmit(selectedIds);
      }}
    >
      {isLoading || pairTagSchema == null ? (
        <CenteredHorizontalThrobber />
      ) : (
        <BioentitySearchView
          limitSelectedEntitiesParams={{
            maxNumEntitiesToAdd: MAX_ANTIBODIES_TO_CREATE,
            selectedEntityName: 'pairs',
          }}
          navSection={Section.MOLBIO_ANTIBODY_NESTED_DATA_CREATION}
          availableTypes={[ModelTypeConstants.BASIC_FOLDER_ITEM]}
          selectedBioentityIds={selectedIds}
          setSelectedBioentityIds={setSelectedIds}
          rowSelectionMode={RowSelectionMode.MULTIPLE}
          initialFilters={[
            {
              ...keyToEmptyFilter(FilterKey.SCHEMA),
              operator: Operator.IS_ONE_OF,
              value: [pairTagSchema.id],
              locked: true,
            },
          ]}
          filterSchemas={getFilterToPairSchemaFunction(pairTagSchema.id)}
        />
      )}
    </Modal>
  );
}
